// Story 8.1 (ADO-1092) — the active-basket session state (AC2/AC3).
//
// Holds the DRAFT basket summary opened for the current cart, so the SPA can
// reflect it (and Story 8.2 can build its line writes on it). Zustand per ADR-004.
// The SERVER is the source of truth (get-or-open); `open` replaces the store with
// whatever the server returns.
//
// Park-and-switch (AC3): switching depot opens that depot's basket (get-or-open);
// the previous depot's DRAFT stays parked server-side. An out-of-order resolve
// (the cart switched again mid-flight) is discarded via the `cart` guard so a
// stale response can never overwrite the current cart's basket.
//
// Story 8.4 (ADO-2013), AC4: the cart is the depot AND the Trading Account
// (`CartTarget`), so changing account at the same Value Centre is a switch too: the
// short-circuit and the stale-resolve guard compare both.

import { create } from 'zustand';
import { openBasket, type BasketSummary } from '../api/basket-init.client';
import { sameCartTarget, type CartTarget } from '../api/cart-target';

export type ActiveBasketStatus = 'idle' | 'opening' | 'ready' | 'error';

interface ActiveBasketState {
  /** The opened DRAFT basket summary, or null when none is open. */
  basket: BasketSummary | null;
  status: ActiveBasketStatus;
  /** The cart the current open is for: guards against stale out-of-order resolves. */
  cart: CartTarget | null;
  /** Open (get-or-open) the basket for `cart` on a choice / switch. */
  open: (cart: CartTarget) => Promise<void>;
  /** Clear the active basket (no depot selected / logout). */
  reset: () => void;
}

export const useActiveBasketStore = create<ActiveBasketState>((set, get) => ({
  basket: null,
  status: 'idle',
  cart: null,

  async open(cart) {
    // Already open for this exact cart: the get-or-open is idempotent server-side,
    // so a remount / re-navigation to the catalogue surface should not re-POST. Only
    // short-circuit on a settled `ready`; an earlier `error`/`idle` still (re)tries.
    const current = get();
    if (current.status === 'ready' && sameCartTarget(current.cart, cart)) return;
    // Mark which cart we're opening for BEFORE the await, so a later switch that
    // supersedes this one is detectable when the response lands.
    set({ status: 'opening', cart });
    const superseded = () => get().cart !== cart;
    try {
      const basket = await openBasket(cart);
      if (superseded()) return; // superseded by a newer switch: discard
      set({ basket, status: 'ready' });
    } catch {
      if (superseded()) return; // superseded: discard the stale failure
      // A failed open does NOT block the UI: the Add gate keys on the SELECTED
      // depot (session state), not on this call succeeding. Surface the error
      // state for a future retry affordance; Story 8.2 owns the durable line write.
      set({ basket: null, status: 'error' });
    }
  },

  reset() {
    set({ basket: null, status: 'idle', cart: null });
  },
}));
